import { supabase } from './supabase';
import type { Country } from './localData';

// Cached AI recommendation for one lab-result SERIES (owner+country+analyte
// [+unit] — same identity as labSeriesKey() in src/lib/labMath.ts), not a
// single reading. One row per series (unique constraint in the migration);
// saving always upserts, so a fresh call replaces the previous cached text
// rather than accumulating history. Cloud-only, same as the recommendation
// feature itself (gated behind `connected` in src/App.tsx).
export interface CachedLabRecommendation {
  summary: string;
  readingCount: number;
  lastTakenOn: string;
  generatedAt: string;
}

type LabSeriesRecommendationRow = {
  summary: string;
  reading_count: number;
  last_taken_on: string;
  generated_at: string;
};

function requireClient() {
  if (!supabase) throw new Error('Supabase не настроен');
  return supabase;
}

export async function loadCachedRecommendation(
  householdId: string,
  owner: string,
  country: Country,
  analyte: string,
  unit?: string
): Promise<CachedLabRecommendation | null> {
  const client = requireClient();
  const { data, error } = await client
    .from('lab_series_recommendations')
    .select('summary, reading_count, last_taken_on, generated_at')
    .eq('household_id', householdId)
    .eq('owner', owner)
    .eq('country', country)
    .eq('analyte', analyte)
    .eq('unit', unit || '')
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const row = data as LabSeriesRecommendationRow;
  return {
    summary: row.summary,
    readingCount: row.reading_count,
    lastTakenOn: row.last_taken_on,
    generatedAt: row.generated_at,
  };
}

export async function saveCachedRecommendation(
  householdId: string,
  userId: string,
  params: { owner: string; country: Country; analyte: string; unit?: string; summary: string; readingCount: number; lastTakenOn: string }
): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('lab_series_recommendations').upsert({
    household_id: householdId,
    owner: params.owner,
    country: params.country,
    analyte: params.analyte,
    unit: params.unit || '',
    summary: params.summary,
    reading_count: params.readingCount,
    last_taken_on: params.lastTakenOn,
    generated_at: new Date().toISOString(),
    generated_by: userId,
  }, { onConflict: 'household_id,owner,country,analyte,unit' });
  if (error) throw error;
}
